// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Consent for course-mapping GPS capture.
//
// Stored per device rather than per user account, deliberately: consent here is to use
// THIS device's GPS and battery for four hours at a time, which is a property of the
// device, not the person. A user with two phones should be able to answer differently on
// each. Capture only exists on Android, so this is also the only place it can be answered.
//
// Default is ON: an unanswered device contributes, which is what grows course coverage.
// Turning it off writes an explicit refusal ('denied') rather than clearing the key —
// with an opt-out default, "never answered" and "said no" must never be confusable.
// The default is not a silent one: Android's own location-permission dialog still gates
// the first capture, recording retires on fully mapped courses, and the battery floor
// still applies.

const STORAGE_KEY = 'goffy:course-trace-consent:v1';

const DENIED = 'denied';

type AsyncStorageLike = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

let storageOverride: AsyncStorageLike | null = null;

const storage = (): AsyncStorageLike | null => storageOverride ?? asyncStorage;

export const getCourseTraceConsent = async (): Promise<boolean> => {
  const store = storage();
  // No storage backend means no way to persist a refusal, so the opt-out
  // default cannot safely apply; it also only happens off-Android, where
  // capture does not exist anyway.
  if (!store) return false;
  try {
    return (await store.getItem(STORAGE_KEY)) !== DENIED;
  } catch {
    // Unreadable storage means a refusal may exist that we cannot see, and
    // honouring a possible "no" beats capturing.
    return false;
  }
};

export const setCourseTraceConsent = async (granted: boolean): Promise<void> => {
  const store = storage();
  if (!store) return;
  // The refusal is stored, never expressed as key removal: a removed key
  // reads as "never answered", which under an on-by-default policy would
  // silently resurrect capture the player turned off.
  await store.setItem(STORAGE_KEY, granted ? 'granted' : DENIED);
};

export const __setCourseTraceConsentStorageForTests = (store: AsyncStorageLike | null) => {
  storageOverride = store;
};
